import {validateFnBySchema} from "@helpers/validate/validateFn";
import {useCallback} from "react";
import {
    buildValidateSchemaMutateSingleVoyages
} from "@containers/singleVoyage/mutateVoyage/saveVoyage/validate/validateSchema";
import {useSnackBar} from "@hooks/alert/useSnackBar";
import {buildErrorsMessages, joinErrorWithLabels} from "@ui/errors/helpers/helpers";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {
    useRootContextMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/context/rootContext/hooks/useRootContextMutateSingleVoyage";
import {
    useViewContextMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/context/viewContext/hooks/useViewContextMutateSingleVoyage";
import {PATH_ACTIVE_SINGLE_VOYAGES, PATH_SCHEDULED_SINGLE_VOYAGES} from "@constants/navigate";
import {
    useDeleteTaskSingleVoyageQuery,
    useSaveTasksSingleVoyageQuery
} from "@containers/singleVoyage/tasks/queryHooks/useTasksSingleVoyageQuery";
import {copyObj} from "@helpers/transform/transform";
import {TaskCreationSingleVoyage} from "@containers/singleVoyage/tasks/types";
import {ISingleVoyage} from "@containers/singleVoyage/types/voyage";
import {
    addDatabaseWaypointsIdsToTasksMutateSingleVoyage
} from "@containers/singleVoyage/mutateVoyage/saveVoyage/helpers/addDatabaseWaypointsIdsToTasksMutateSingleVoyage";
import {refetchTasksSingleVoyage} from "@containers/singleVoyage/tasks/helpers/refetchTasks";
import {useSaveVoyageQuery} from "@containers/singleVoyage/actionsSingleVoyage/queryHooks/useActionsSingleVoyageQuery";
import {
    buildCreationWayPointsByPointsWithCorrection
} from "@containers/singleVoyage/mutateVoyage/saveVoyage/helpers/buildCreationWayPointsByPointsWithCorrection";

const validateSchema = buildValidateSchemaMutateSingleVoyages()

export function useSaveVoyageMutateSingleVoyage(): {
    isPending: boolean,
    onSave: () => void
} {
    const {t} = useTranslation()
    const navigate = useNavigate();
    const {setSnackBar} = useSnackBar()

    const {
        routeInfo,
        points,
        positions,
        voyageId,
        tasks,
        tasksLocalDeleteIds,
        tasksLocalCreateIds,
        accountId,
    } = useRootContextMutateSingleVoyage()

    const {
        setModeSecondarySidebar,
        miniModeSecondarySidebar,
        setMiniModeSecondarySidebar,
        setTouchedEditRouteInfo,
    } = useViewContextMutateSingleVoyage()

    const {
        isPending: isPendingTasks,
        mutate: saveTasks
    } = useSaveTasksSingleVoyageQuery({
        onSuccess: () => {
            navigate(PATH_SCHEDULED_SINGLE_VOYAGES);
        },
        onError: (error) => {
            const message = buildErrorsMessages(error)
            setSnackBar({
                message: `${t('singleVoyage.errorCreateTasks')}.${message}`,
                variant: 'error'
            })
            const isNotPlanned = routeInfo.status !== 'READY_FOR_MANUAL_RUN' && routeInfo.status !== 'PLANNED_FOR_AUTORUN'
            navigate(isNotPlanned ? PATH_ACTIVE_SINGLE_VOYAGES : PATH_SCHEDULED_SINGLE_VOYAGES)
        }
    })

    const {
        mutateAsync: deleteTask,
        isPending: isPendingDeleteTask
    } = useDeleteTaskSingleVoyageQuery({
        hasRefetch: false,
        hasSnackbar: false,
    })
    const onDeleteTasks = async () => {
        if (!tasksLocalDeleteIds?.length) {
            return
        }

        for (const id of tasksLocalDeleteIds) {
            try {
                await deleteTask({
                    id,
                    voyageId,
                    accountId,
                })
            } catch (error) {
            }
        }

    }
    const onSaveTasks = async (singleVoyage: ISingleVoyage) => {

        if (!tasks?.length) {
            return
        }
        const addedTasks: TaskCreationSingleVoyage[] = []
        const updatedTasks: TaskCreationSingleVoyage[] = []
        tasks.forEach((task) => {
            if (
                !tasksLocalCreateIds.includes(Number(task.id))
            ) {
                updatedTasks.push(task)
                return
            }
            const copyTask = copyObj(task)
            delete copyTask.id
            addedTasks.push(copyTask)
        })

        // Updated tasks need the mapping too: a freshly created stop may have been
        // added to an already saved task, and it still carries a local id.
        let newTasks: TaskCreationSingleVoyage[]
        try {
            newTasks = addDatabaseWaypointsIdsToTasksMutateSingleVoyage({
                pointsDatabase: singleVoyage.waypoints,
                pointsLocal: points,
                tasks: [...addedTasks, ...updatedTasks]
            })
        } catch (error) {
            // The voyage is saved, the tasks are not. Staying silent is not an option:
            // to the dispatcher a silent failure looks like nothing happened at all.
            setSnackBar({
                message: error instanceof Error ? error.message : String(error),
                variant: 'error'
            })
            return
        }

        saveTasks({
            tasks: newTasks,
            accountId,
            voyageId: singleVoyage.id,
        })
    }
    const {
        isPending,
        mutate: save
    } = useSaveVoyageQuery({
        onSuccess: async (singleVoyage) => {
            if (tasksLocalDeleteIds?.length >= 1) {
                await onDeleteTasks()

            }
            if (!tasks?.length) {
                refetchTasksSingleVoyage()
                const isNotPlanned = routeInfo.status !== 'READY_FOR_MANUAL_RUN' && routeInfo.status !== 'PLANNED_FOR_AUTORUN'
                navigate(isNotPlanned ? `${PATH_ACTIVE_SINGLE_VOYAGES}?accountId=${accountId}` : `${PATH_SCHEDULED_SINGLE_VOYAGES}?accountId=${accountId}`)
                return
            }
            await onSaveTasks(singleVoyage)
        }
    })

    const onSave = useCallback(async () => {
        setTouchedEditRouteInfo(true)
        const {
            errorMessage,
            error
        } = validateFnBySchema({
            value: {
                wayPoints: points,
                routeInfo: {
                    ...routeInfo,
                    accountId
                },
            },
            validateSchema,
        })
        if (error && errorMessage) {
            const errorMessageStr = joinErrorWithLabels(errorMessage, {
                accountId: 'base.carrier',
                driverId: 'base.driver',
                deviceId: 'base.device',
                title: 'base.name',
                routeInfo: 'base.routeInfo',
                wayPoints: 'base.points',
            })
            if (!errorMessage?.wayPoints && errorMessage?.routeInfo) {
                setModeSecondarySidebar('route')
                if (miniModeSecondarySidebar) {
                    setMiniModeSecondarySidebar(false)
                }
            }

            const message = `${t('base.fixError')} : ${errorMessageStr}`

            setSnackBar({
                message,
                variant: 'error'
            })
            return
        }

        save({
            id: voyageId,
            accountId,
            title: routeInfo.title,
            deviceId: routeInfo.deviceId,
            driverId: routeInfo.driverId ? routeInfo.driverId : null,
            plannedStart: routeInfo.plannedStart,
            plannedEnd: routeInfo.plannedEnd,
            wayPoints: buildCreationWayPointsByPointsWithCorrection(points),
            routeShape: {
                bufferRadius: 50,
                distance: 1,
                geom: {
                    type: 'LineString',
                    coordinates: positions.map((it) => {
                        return [
                            it.lng, it.lat
                        ]
                    }),
                },
            },
            config: routeInfo.config,
            status: routeInfo.status
        })
    }, [
        routeInfo,
        points,
        positions,
        voyageId,
    ])

    return {
        onSave,
        isPending: isPending || isPendingTasks || isPendingDeleteTask
    }
}
